import { useMemo, useState } from 'react'
import { buildTree, type Category } from './categoryTree'
import { compareNodes, filterNodes, SORTS, type SortKey } from './categorySort'

/**
 * Everything the categories screen derives from its rows, in one place.
 *
 * ## Why this is a hook and not state inside `CategoryTable`
 *
 * It used to live in the table, which meant the toolbar lived there too — and
 * `CategoryTable` renders only inside `<Async>`'s SUCCESS branch. So the
 * filter, the sort and "New category" all vanished while the list was loading
 * and on an error, and the only ways out were both worse: a second copy of the
 * button outside `Async` (which moves between containers as data lands and
 * detaches a click already in flight — a test caught exactly that), or a
 * separate CTA row.
 *
 * With the state here the ROUTE owns it, renders one toolbar unconditionally,
 * and hands the derived view down. Same shape `Merchants` already had, where
 * `MerchantsToolbar` is a sibling of the panel rather than a child of it.
 *
 * ## One computation, not two
 *
 * `expandable` is needed by the toolbar ("Expand all") and `childrenById` by
 * the table, and both come from the same filtered, sorted tree. Deriving that
 * tree in two places would be the real cost of splitting them — 262 rows
 * rebuilt on every keystroke, twice. It is built once here and both consumers
 * read it.
 */

export function useCategoryView(categories: Category[], mostUsed: readonly string[]) {
  const [query, setQuery] = useState('')
  // Ids of the open rows. A Set of ids rather than a flag on the node: the
  // tree is rebuilt from the server list on every render, so state hung off a
  // node object would be discarded by the next refetch.
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set())
  const [sort, setSort] = useState<SortKey>('name')

  // Built once per ranking change rather than per comparison: a comparator
  // doing `indexOf` would be O(n·8) over 262 rows on every keystroke in the
  // filter box.
  const rank = useMemo(() => new Map(mostUsed.map((id, i) => [id, i])), [mostUsed])

  /** No ranking, no segment — see `SORTS`. A caller with no transactions and a
   *  backend that predates the field both arrive here with an empty list. */
  const sorts = useMemo(
    () => (mostUsed.length > 0 ? SORTS : SORTS.filter((s) => s.key !== 'used')),
    [mostUsed],
  )

  const segments = useMemo(
    () =>
      buildTree(categories).map((segment) => ({
        ...segment,
        nodes: filterNodes(segment.nodes, query).sort((a, b) => compareNodes(a, b, sort, rank)),
      })),
    [categories, query, sort, rank],
  )

  const expandable = useMemo(
    () => segments.flatMap((s) => s.nodes.filter((n) => n.children.length > 0)),
    [segments],
  )
  const allOpen = expandable.length > 0 && expandable.every((n) => open.has(n.category.id))
  const childrenById = useMemo(
    () =>
      new Map(segments.flatMap((s) => s.nodes.map((n) => [n.category.id, n.children] as const))),
    [segments],
  )

  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev)
      if (!next.delete(id)) next.add(id)
      return next
    })

  const toggleAll = () =>
    setOpen(allOpen ? new Set() : new Set(expandable.map((n) => n.category.id)))

  return {
    /** Every row, unfiltered — the delete picker needs targets the search box has hidden. */
    categories,
    query,
    setQuery,
    sort,
    setSort,
    sorts,
    segments,
    expandable,
    allOpen,
    open,
    toggle,
    toggleAll,
    childrenById,
  }
}

/**
 * DERIVED from the hook, never hand-written beside it.
 *
 * A restated shape here would drift the moment the hook returns something
 * new — and the first draft of this file did exactly that, declaring
 * `childrenById` as `Map<string, CategoryNode[]>` when the tree actually
 * carries `Category[]`, which typechecked here and failed at the consumer.
 */
export type CategoryView = ReturnType<typeof useCategoryView>
